if (!customElements.get('product-free-gifts')) {
  customElements.define(
    'product-free-gifts',
    class ProductFreeGifts extends HTMLElement {
      constructor() {
        super();
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleMainVariantChange = this.handleMainVariantChange.bind(this);
        this.handleMainColorChange = this.handleMainColorChange.bind(this);
      }

      connectedCallback() {
        this.formId = this.dataset.productFormId;
        this.propertiesContainer = this.querySelector('[data-free-gift-properties]');
        this.items = this.querySelectorAll('[data-free-gift-item]');

        this.initItems();
        this.syncAllGiftsToMainColor();
        this.bindMainVariantListeners();
        document.addEventListener('click', this.handleDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
        this.unbindMainVariantListeners();
      }

      bindMainVariantListeners() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleMainVariantChange
          );
        }

        this.mainColorSelect = document.querySelector('[data-native-color-select]');
        this.mainColorSelect?.addEventListener('change', this.handleMainColorChange);

        this.mainOptionSelects = Array.from(
          document.querySelectorAll('variant-selects select[name^="options["]')
        ).filter((select) => {
          const label = select.name.match(/options\[(.+)\]/)?.[1] || '';
          return /couleur|color/i.test(label);
        });

        this.mainOptionSelects.forEach((select) => {
          select.addEventListener('change', this.handleMainColorChange);
        });
      }

      unbindMainVariantListeners() {
        this.variantChangeUnsubscriber?.();
        this.mainColorSelect?.removeEventListener('change', this.handleMainColorChange);
        this.mainOptionSelects?.forEach((select) => {
          select.removeEventListener('change', this.handleMainColorChange);
        });
      }

      handleMainVariantChange(event) {
        const variant = event?.data?.variant;
        const product = this.getMainProductJson();
        const colorValue = this.getColorFromVariant(variant, product) || this.getMainProductColor();
        this.syncAllGiftsToMainColor(colorValue);
      }

      handleMainColorChange() {
        this.syncAllGiftsToMainColor(this.getMainProductColor());
      }

      getForm() {
        return this.formId ? document.getElementById(this.formId) : null;
      }

      getMainProductJson() {
        const productJson = document.querySelector('[type="application/json"][data-product-json]');
        if (!productJson) return null;

        try {
          return JSON.parse(productJson.textContent);
        } catch (error) {
          console.warn('JSON produit principal invalide.', error);
          return null;
        }
      }

      getColorOptionIndex(product) {
        if (!product?.options?.length) return -1;

        return product.options.findIndex((option) => {
          const name = typeof option === 'string' ? option : option?.name;
          return /couleur|color/i.test(name || '');
        });
      }

      getColorFromVariant(variant, product) {
        if (!variant) return null;

        const colorIndex = this.getColorOptionIndex(product);
        if (colorIndex !== -1) {
          return variant[`option${colorIndex + 1}`] || null;
        }

        if (variant.title && variant.title !== 'Default Title') {
          return variant.title;
        }

        return variant.option1 || null;
      }

      getMainProductColor() {
        const colorSelect = document.querySelector('[data-native-color-select]');
        if (colorSelect?.value) return colorSelect.value;

        const optionSelect = Array.from(
          document.querySelectorAll('variant-selects select[name^="options["]')
        ).find((select) => {
          const label = select.name.match(/options\[(.+)\]/)?.[1] || '';
          return /couleur|color/i.test(label);
        });
        if (optionSelect?.value) return optionSelect.value;

        const colorLabel = document.querySelector('[data-color-current-label]');
        if (colorLabel?.textContent.trim()) return colorLabel.textContent.trim();

        const selectedVariantEl = document.querySelector('variant-selects [data-selected-variant]');
        const product = this.getMainProductJson();

        if (selectedVariantEl && product) {
          try {
            const variant = JSON.parse(selectedVariantEl.textContent);
            return this.getColorFromVariant(variant, product);
          } catch (error) {
            console.warn('Variante sélectionnée invalide.', error);
          }
        }

        return null;
      }

      normalizeColor(value) {
        if (!value) return '';

        return value
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
      }

      getDefaultGiftVariant(giftData) {
        const defaultVariant = giftData.variants?.find(
          (variant) => String(variant.id) === String(giftData.defaultVariantId)
        );

        if (defaultVariant?.available) return defaultVariant;

        return giftData.variants?.find((variant) => variant.available) || giftData.variants?.[0] || null;
      }

      findMatchingGiftVariant(giftData, colorValue) {
        if (!giftData?.variants?.length) return null;

        const normalizedColor = this.normalizeColor(colorValue);
        if (!normalizedColor) return this.getDefaultGiftVariant(giftData);

        const matches = giftData.variants.filter((variant) => {
          const optionValues = [variant.option1, variant.option2, variant.option3]
            .filter(Boolean)
            .map((option) => this.normalizeColor(option));

          if (optionValues.includes(normalizedColor)) return true;

          return this.normalizeColor(variant.title) === normalizedColor;
        });

        const availableMatch = matches.find((variant) => variant.available);
        if (availableMatch) return availableMatch;
        if (matches.length) return matches[0];

        return this.getDefaultGiftVariant(giftData);
      }

      syncAllGiftsToMainColor(colorValue = this.getMainProductColor()) {
        this.items.forEach((item) => {
          const giftData = this.getGiftData(item);
          if (!giftData) return;

          const matchedVariant = this.findMatchingGiftVariant(giftData, colorValue);
          if (!matchedVariant) return;

          this.applyVariantSelection(item, matchedVariant.id);
        });
      }

      initItems() {
        this.items.forEach((item) => {
          if (item.dataset.freeGiftInit === 'true') return;
          item.dataset.freeGiftInit = 'true';

          const select = item.querySelector('[data-free-gift-select]');
          const trigger = item.querySelector('[data-free-gift-trigger]');
          const dropdown = item.querySelector('[data-free-gift-dropdown]');

          if (trigger && dropdown) {
            trigger.addEventListener('click', (event) => {
              event.stopPropagation();
              this.closeAllSelects(select);
              select.classList.toggle('is-open');
              const isOpen = select.classList.contains('is-open');
              trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
              dropdown.hidden = !isOpen;
            });

            dropdown.querySelectorAll('[data-variant-id]').forEach((option) => {
              option.addEventListener('click', () => {
                if (option.disabled) return;
                this.selectVariant(item, option);
                this.closeAllSelects();
              });
            });
          }
        });
      }

      closeAllSelects(exceptSelect = null) {
        this.querySelectorAll('[data-free-gift-select]').forEach((select) => {
          if (select === exceptSelect) return;

          select.classList.remove('is-open');
          const trigger = select.querySelector('[data-free-gift-trigger]');
          const dropdown = select.querySelector('[data-free-gift-dropdown]');

          if (trigger) trigger.setAttribute('aria-expanded', 'false');
          if (dropdown) dropdown.hidden = true;
        });
      }

      handleDocumentClick(event) {
        if (!this.contains(event.target)) {
          this.closeAllSelects();
        }
      }

      applyVariantSelection(item, variantId) {
        const option = item.querySelector(`[data-variant-id="${variantId}"]`);

        if (option && !option.disabled) {
          this.selectVariant(item, option);
          return;
        }

        const giftData = this.getGiftData(item);
        const variant = giftData?.variants?.find((entry) => String(entry.id) === String(variantId));
        if (!variant) return;

        this.selectVariant(item, {
          dataset: {
            variantId: String(variant.id),
            variantTitle: variant.title,
            variantImage: variant.image || '',
          },
        });
      }

      selectVariant(item, option) {
        const variantId = option.dataset.variantId;
        const variantSku = option.dataset.variantSku;
        const variantTitle = option.dataset.variantTitle;
        const variantImage = option.dataset.variantImage;
        const variantInput = item.querySelector('[data-free-gift-variant-id]');
        const label = item.querySelector('[data-free-gift-label]');
        const swatch = item.querySelector('[data-free-gift-swatch]');
        const image = item.querySelector('.free-gifts__image');
        const dropdown = item.querySelector('[data-free-gift-dropdown]');

        if (variantInput) {
          variantInput.value = variantId;
          variantInput.setAttribute('data-sku', variantSku);
        }
        if (label) label.textContent = variantTitle;

        if (swatch) {
          if (variantImage) {
            swatch.innerHTML = `<img src="${variantImage}" alt="${variantTitle}" class="free-gifts__swatch-image" loading="lazy" width="40" height="40">`;
          } else {
            swatch.innerHTML = '';
          }
        }

        if (image && variantImage) {
          image.src = variantImage;
          image.srcset = '';
        }

        dropdown?.querySelectorAll('[data-variant-id]').forEach((button) => {
          const isActive = button.dataset.variantId === variantId;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.syncProperties();
      }

      getGiftData(item) {
        const jsonEl = item.querySelector('[data-free-gift-json]');
        if (!jsonEl) return null;

        try {
          return JSON.parse(jsonEl.textContent);
        } catch (error) {
          console.error('JSON cadeau offert invalide', error);
          return null;
        }
      }

      syncProperties() {
        if (!this.propertiesContainer) return;

        this.propertiesContainer.innerHTML = '';
        const form = this.getForm();

        this.items.forEach((item, index) => {
          const data = this.getGiftData(item);
          const variantInput = item.querySelector('[data-free-gift-variant-id]');
          const variantId = variantInput?.value;
          const variantSku = variantInput?.dataset.sku;
          if (!data || !variantId) return;

          const variant = data.variants?.find((entry) => String(entry.id) === String(variantId));
          let variantTitle = variant?.title || '';
          if (variantTitle === 'Default Title') {
            variantTitle = '';
          }
          const giftName = item.dataset.giftName || data.giftName || data.productTitle;
          const quantity = item.dataset.giftQuantity || data.quantity || 1;
          const propertyLabel = `Offert - ${giftName}`;
          const propertyValue = `${variantTitle === '' ? `x${quantity}` : `${variantTitle} x${quantity}`}`;

          this.appendPropertyInput({
            name: `properties[_free_gift_${index}_variant_sku]`,
            value: variantSku,
            form,
          });

          this.appendPropertyInput({
            name: `properties[_free_gift_${index}_variant_id]`,
            value: variantId,
            form,
          });

          this.appendPropertyInput({
            name: `properties[_free_gift_${index}_quantity]`,
            value: quantity,
            form,
          });

          this.appendPropertyInput({
            name: `properties[_free_gift_${index}_product_id]`,
            value: data.productId,
            form,
          });

          this.appendPropertyInput({
            name: `properties[${propertyLabel}]`,
            value: propertyValue,
            form,
          });
        });
      }

      appendPropertyInput({ name, value, form }) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.setAttribute('data-free-gift-property', '');

        if (form) {
          input.setAttribute('form', this.formId);
        }

        this.propertiesContainer.appendChild(input);
      }
    }
  );
}
